import type React from "react";
import { type FC, type ReactNode } from "react";
import classes from "~/features/BottomNavigation/BottomNavigation.module.less";
import { Badge, Paper } from "~/common/mui";
import { NOTIFICATIONS_PAGE, PRODUCT_COMPARISON_PAGE, STORE_FINDER, WISHLIST_PAGE } from "~/utils/url/staticUrls";
import { canSeeSalesFinder } from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { getProductComparisonNavigationEntryUrl } from "~/features/compareProducts/utils/productComparisonHelper";
import { useDispatch, useSelector } from "react-redux";
import { selectProductComparisonList } from "~/features/compareProducts/redux/productComparisonSelectors";
import { getWishlistNavigationEntryUrl, useLoadWishlistsOnMount } from "~/features/wishlist/utils/wishlistHelper";
import { selectWishlistAmount } from "~/features/wishlist/redux/wishlistSelectors";
import { selectNewNotificationAmount } from "~/features/notifications/redux/NotificationsSelectors";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import classNames from "classnames";
import { markNotificationsAsInteractedThunk } from "~/features/notifications/redux/actions";
import { useTranslation } from "react-i18next";
import { canSeeWishlistButton } from "~/utils/features";
import { MobileMinicart } from "~/features/cart/minicart/MinicartComponent";
import { useIsBottomNavigationVisible } from "~/features/BottomNavigation/BottomNavigationUtils";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CompareOutlinedIcon from "@mui/icons-material/CompareOutlined";
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined";

const PRODUCT_COMPARISON_URL = getProductComparisonNavigationEntryUrl();
const WISHLIST_URL = getWishlistNavigationEntryUrl();

function BottomNavigationSection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const listItems = useSelector(selectProductComparisonList).length;

  const showWishlistMenuEntry = canSeeWishlistButton();
  useLoadWishlistsOnMount();
  const wishlistAmount = useSelector(selectWishlistAmount);

  const newNotificationAmount = useSelector(selectNewNotificationAmount);

  const canSeeCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);

  if (!useIsBottomNavigationVisible()) {
    return null;
  }

  return (
    <Paper variant={"elevation"} elevation={3} className={classes.mainContainer}>
      <ul className={classes.list}>
        <li className={classes.listItemContainer}>
          <a href={PRODUCT_COMPARISON_URL} id="navigation_comparison_list_btn" data-test-id={"navigation-wishlist"}>
            <BottomNavigationItem
              data-test-id={"navigation-btn-sales-partner"}
              quantity={listItems}
              icon={<CompareOutlinedIcon />}
              name={t("header.link.comparison.list")}
              isActive={window.location.pathname.endsWith(PRODUCT_COMPARISON_PAGE)}
            />
          </a>
        </li>
        {showWishlistMenuEntry && (
          <li className={classes.listItemContainer}>
            <a href={WISHLIST_URL} id="navigation_wishlist_btn" data-test-id={"navigation-wishlist"}>
              <BottomNavigationItem
                name={t("wishlistPage.title")}
                icon={<BookmarkBorderOutlinedIcon />}
                data-test-id={"navigation-btn-wishlist"}
                quantity={wishlistAmount !== undefined ? wishlistAmount : ""}
                isActive={window.location.pathname.endsWith(WISHLIST_PAGE)}
              />
            </a>
          </li>
        )}
        {userHasPermission(Permission.SEE_NOTIFICATIONS) && (
          <li className={classes.listItemContainer}>
            <a href={NOTIFICATIONS_PAGE} id="navigation_notification_btn" data-test-id={"navigation-notification"}>
              <BottomNavigationItem
                name={t("header.link.my.notification")}
                data-test-id={"navigation-btn-notification"}
                icon={<NotificationsOutlinedIcon />}
                quantity={newNotificationAmount}
                onClick={() => dispatch(markNotificationsAsInteractedThunk())}
                isActive={window.location.pathname.endsWith(NOTIFICATIONS_PAGE)}
                isNotification
              />
            </a>
          </li>
        )}
        {canSeeSalesFinder() && (
          <li className={classes.listItemContainer}>
            <a href={STORE_FINDER} data-test-id={"navigation-sales-partner"}>
              <BottomNavigationItem
                data-test-id={"navigation-btn-sales-partner"}
                icon={<LocationOnOutlinedIcon />}
                name={t("salesPartner.title")}
                isActive={window.location.pathname.endsWith(STORE_FINDER)}
              />
            </a>
          </li>
        )}
        {canSeeCart && (
          <li className={classes.listItemContainer}>
            <MobileMinicart />
          </li>
        )}
      </ul>
    </Paper>
  );
}

interface BottomNavigationItemProps {
  name: string;
  icon?: ReactNode;
  isActive?: boolean;
  isNotification?: boolean;
  quantity?: string | number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  "data-test-id"?: string;
}

export const BottomNavigationItem: FC<BottomNavigationItemProps> = (props) => (
  <div
    onClick={props.onClick}
    data-test-id={props["data-test-id"]}
    className={classNames(
      classes.listItem,
      props.isNotification && classes.listItemNotification,
      props.isActive && classes.listItemActive,
    )}
  >
    <>
      <Badge badgeContent={props.quantity ? props.quantity : null} className={classes.badge}>
        {props.icon && props.icon}
      </Badge>
      {props.name && <div className={"label-medium f-no-wrap"}>{props.name}</div>}
    </>
  </div>
);

export function createBottomNavigation() {
  return <BottomNavigationSection />;
}
